import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Radar, Briefcase, Bookmark, FileText, Settings, LogOut, PlusCircle, Clock, LayoutDashboard, Users, Building2, Send } from "lucide-react";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuth } from "@/context/AuthContext";
import { isEmployer, isAgency } from "@/lib/roles";
import { Button } from "@/components/ui/button";

export default function Navbar({ showLanguage = false }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const employer = isEmployer(user?.role);
  const agency = isAgency(user?.role);
  const handleLogout = () => { logout(); navigate("/"); };

  const seekerLinks = [
    { to: "/jobs", label: t("nav.jobs"), icon: Briefcase, testid: "nav-jobs" },
    { to: "/radar", label: t("nav.radar"), icon: Radar, testid: "nav-radar" },
    { to: "/saved", label: t("nav.saved"), icon: Bookmark, testid: "nav-saved" },
    { to: "/applications", label: t("nav.applications"), icon: FileText, testid: "nav-applications" },
  ];
  const employerLinks = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/talent", label: "Talent", icon: Users, testid: "nav-talent" },
    { to: "/saved-candidates", label: "Pool", icon: Bookmark, testid: "nav-saved-candidates" },
    { to: "/applicants", label: t("nav.applicants"), icon: Radar, testid: "nav-applicants" },
    { to: "/post-job", label: "Post", icon: PlusCircle, testid: "nav-post-job" },
  ];
  const agencyLinks = [
    { to: "/clients", label: "Clients", icon: Building2, testid: "nav-clients" },
    { to: "/submissions", label: "Submissions", icon: Send, testid: "nav-submissions" },
  ];
  const links = employer ? [...employerLinks, ...(agency ? agencyLinks : [])] : seekerLinks;

  return (
    <nav className="sticky top-0 z-40 liquid-glass border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <Logo />
          {user && (
            <div className="hidden lg:flex items-center gap-0.5">
              {links.map((l) => {
                const active = location.pathname === l.to;
                return (
                  <Link key={l.to} to={l.to} data-testid={l.testid}
                    className={`px-2.5 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${active ? "text-teal-300 bg-teal-500/10" : "text-slate-400 hover:text-slate-100 hover:bg-white/5"}`}>
                    <l.icon size={15} className="shrink-0" /> {l.label}
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* right cluster */}
        <div className="flex items-center gap-1.5">
          {showLanguage && <LanguageSwitcher showName label={t("common.language")} />}
          {user ? (
            <>
              {employer && user.trial?.on_trial && (
                <Link to="/upgrade" data-testid="nav-trial-pill"
                  className={`hidden md:inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full border transition-colors ${user.trial.active ? "text-teal-300 border-teal-500/30 bg-teal-500/10 hover:bg-teal-500/15" : "text-amber-300 border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/15"}`}>
                  <Clock size={13} /> {user.trial.active ? t("trial.daysLeft", { n: user.trial.days_left }) : t("trial.ended")}
                </Link>
              )}
              <NotificationBell />
              <Link to="/settings" data-testid="nav-settings" className="hidden lg:inline-flex p-2 rounded-lg text-slate-400 hover:text-teal-300 hover:bg-white/5 transition-colors">
                <Settings size={18} />
              </Link>
              <Link to="/profile" data-testid="nav-profile" className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/5 transition-colors">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-blue-600 flex items-center justify-center text-xs font-bold text-slate-900">
                  {user.name?.[0]?.toUpperCase() || "U"}
                </div>
                <span className="hidden lg:inline text-sm text-slate-200 max-w-[120px] truncate">{user.name}</span>
              </Link>
              <button onClick={handleLogout} data-testid="nav-logout" className="hidden lg:inline-flex p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-white/5 transition-colors">
                <LogOut size={18} />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="hidden sm:inline-flex">
                <Button variant="ghost" className="text-slate-300 hover:text-white">{t("nav.login")}</Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button className="bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold hover:opacity-90">{t("nav.getStarted")}</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
